import { action, observable, computed, makeObservable, reaction } from 'mobx';
import { ChannelEditForm } from 'modules/channel/forms';
import ChannelStore from './ChannelStore';
import { BaseViewStore } from 'core/stores';
import {
	translate,
	isSome,
	notificationError,
	isSuperAdminOrganization,
	formatDate,
	isCurrentDateGreater,
	convertTimeToUTC,
	isSmartshipWarehouseAdministrator,
	isShipStationCarrierService,
} from 'core/utils';
import {
	channelType,
	channelTypesWithoutRequiredCarriers,
	sourceOfChange,
	sellerCloudChannels,
	carrierTypes,
	shipStationCarrierTypes,
	getShopifyOAuthUrl,
} from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { LookupStore } from 'modules/lookup/stores';
import { CarrierStore, CarrierServiceStore } from 'modules/carrier/stores';
import { lookupRouteName, carrierChannelTypeEnum } from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import { walmartHandoff } from 'common/utils';
import { localStorageHandler } from 'core/services';
import { CredentialsValidationStore } from 'modules/credentials-validation/stores';
import { UpdateInventoryStore } from 'modules/product/stores';
import { RestockDate } from 'modules/restock-date/pages';
import { CustomLabel, WarehouseCodes, AmazonTemplates, ChannelExcludedCarrierServices } from '../pages';
import _ from 'lodash';
import moment from 'moment';
import UpdateInventoryLog from 'modules/product/pages/UpdateInventoryLog';
import md5 from 'md5';

class ChannelEditViewStore extends BaseViewStore {
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable scheduledRestockDate = null;
	@observable buyShippingDropdowns = [];
	@observable trackingCarrierDropdowns = [];
	@observable showDuplicateExludeOrderParameterKeyError = false;
	@observable showEmptyExcludeOrderParameterValueError = false;
	@observable showEmptyExcludeOrderParameterKeyError = false;
	// Carrier service ids this channel EXCLUDES. Seeded from the channel on edit, empty on create.
	// The modal presents the complement ("allowed"); this is what gets persisted.
	@observable excludedCarrierServiceIds = [];
	// Ids of the carrier services this channel can actually use, scoped to the carrier types of its required
	// carriers. Ids rather than a bare total because excludedCarrierServiceIds can hold services from carrier
	// types that are no longer required, and those must not count against the allowed number.
	@observable inScopeCarrierServiceIds = [];
	// Observable because ChannelEditTemplate destructures canEditExcludedCarrierServices before its loading
	// guard, so those computeds first run while initializeDropdowns has not assigned these yet. A plain
	// property would give them nothing to track and they would stay cached at their initial value.
	@observable channelTypeDropdownStore = null;
	@observable carrierChannelDropdownStore = null;
	convertWarehouses = [];
	amazonTemplatesModel = {
		amazonTemplates: [],
		defaultPrimeTemplate: '',
	};
	warehouses = [];
	isEdit = false;
	channel;
	amazonOAuthStateHash = null;
	oAuthDate = null;
	form = new ChannelEditForm({
		onSuccess: async () => {
			await this.createOrUpdateChannel();
		},
	});
	shipmentDocumentPullSourceOptions = [{ value: 0, label: 'SellerCloud' }, { value: 1, label: 'Banyan' }];
	// Option value is the string we persist, so no label/number round trip is needed on save.
	additionalDocumentPullSourceOptions = [{ value: 'SellerCloud', label: 'SellerCloud' }];

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ChannelStore(rootStore);
		this.credentialsValidationStore = new CredentialsValidationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.carrierStore = new CarrierStore(rootStore);
		this.carrierServiceStore = new CarrierServiceStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.isEdit = isSome(rootStore.routerStore.routerState.params.id);
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		if (this.isEdit) await this.getChannel();
		await Promise.all([this.getCarrierTypes(), this.getWarehouses(), this.initializeDropdowns()]);
		// Depends on the required carriers dropdown, so it cannot run alongside initializeDropdowns.
		await this.getInScopeCarrierServices();
		this.setFieldRules();
		this.initializeReactions();
		this.handleBuyShippingCarriers();
		await this.initializeTrackingCarrierDropdowns();
		this.oAuthDate = new Date().toISOString();
		this.amazonOAuthStateHash = md5(this.rootStore.app.baasic.userHandler.user.user.id + this.oAuthDate);
		await this.initializeCustomAttributes();
		this.resume();
	}

	@action.bound
	initializeTrackingCarrierDropdowns = async () => {
		if (this.trackingCarrierDropdowns.length === 0) {
			_.each(
				this.carrierTypes.filter(ct => ct.abrv !== 'ltl' && ct.abrv !== 'passport' && ct.abrv !== 'ontrac'),
				carrierType => {
					const fieldName = `${carrierType.abrv.toLowerCase()}TrackingCarrier`;
					this.form.add({
						name: fieldName,
						rules: '',
						label: `CHANNEL.TRACKING.${carrierType.abrv.toUpperCase()}`,
					});
					const dropdownStore = new BaasicDropdownViewStore({
						field: this.form.$(fieldName),
						store: new CarrierStore(this.rootStore),
						filterProperties: {
							sellerIds: _.get(this.sellerDropdownStore, 'value') ? [this.sellerDropdownStore.value.value] : null,
							carrierTypeIds: [carrierType.id],
							embed: ['carrierType'],
						},
					});
					this.trackingCarrierDropdowns.push(dropdownStore);
				}
			);
		}

		if (this.isEdit) {
			this.updateCarrierFields(
				_.filter(this.channel.carrierChannels, item => {
					return item.carrierChannelType == carrierChannelTypeEnum.trackingCarrier;
				}),
				'TrackingCarrier'
			);
		}
	};

	@action.bound
	initializeDropdowns = async () => {
		if (this.isSuperAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore();
			await this.sellerDropdownStore.initialize({
				field: this.form.$('sellerId'),
				value: this.isEdit ? this.channel.seller : localStorageHandler.get('selectedSeller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: this.onSellerChange,
			});
			this.form.$('sellerId').set('rules', 'required');
		}
		_.each(
			['sellerCloudDropdownStore', 'channelTypeDropdownStore', 'timezoneDropdownStore', 'carrierChannelDropdownStore'],
			dropdownStore => {
				this[dropdownStore] = new BaasicDropdownViewStore();
			}
		);
		await Promise.all([
			this.sellerCloudDropdownStore.initialize({
				field: this.form.$('channelIds'),
				isMulti: true,
				value: this.setSellerCloudDropdownValue(),
				staticOptions: sellerCloudChannels,
			}),
			this.channelTypeDropdownStore.initialize({
				field: this.form.$('channelTypeId'),
				value: _.get(this, 'channel.channelType'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.channelType,
				onChangeCallback: async option => {
					await this.onChannelTypeChange(option);
				},
				initCallback: async () => {
					await this.onChannelTypeInitialize();
				},
			}),
			this.timezoneDropdownStore.initialize({
				field: this.form.$('timezoneId'),
				value: _.get(this.channel, 'timezone'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.timezone,
				itemTemplate: {
					value: 'id',
					label: 'abrv',
				},
			}),
			this.carrierChannelDropdownStore.initialize({
				field: this.form.$('carrierChannelIds'),
				value: this.getRequiredCarriers(),
				store: new CarrierStore(this.rootStore),
				filterProperties: {
					sellerIds: _.get(this.sellerDropdownStore, 'value') ? [this.sellerDropdownStore.value.value] : null,
					embed: ['carrierType'],
				},
				isMulti: true,
				fetchInitialValues: !this.isSuperAdmin || _.get(this.sellerDropdownStore, 'value') != null,
				onChangeCallback: async () => {
					this.removeBuyShippingCarrier();
					_.forEach(this.buyShippingDropdowns, dropdown => {
						dropdown.reload();
					});
					await this.onRequiredCarriersChange();
				},
			}),
		]);
		this.shipmentDocumentPullSourceDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('shipmentDocumentPullSource'),
			staticOptions: this.getShipmentDocumentPullSourceOptions(),
		});

		// If channel already has a saved shipmentDocumentPullSource (string), select matching option
		const _savedShipmentSource = _.get(this, 'channel.shipmentDocumentPullSource');
		if (!_.isNil(_savedShipmentSource)) {
			const opt = this.shipmentDocumentPullSourceOptions.find(
				o => String(o.value) === String(_savedShipmentSource) || o.label === _savedShipmentSource
			);
			if (opt) {
				this.shipmentDocumentPullSourceDropdownStore.setValue(opt);
			}
		}

		this.additionalDocumentPullSourceDropdownStore = new BaasicDropdownViewStore({
			field: this.form.$('additionalDocumentPullSource'),
			staticOptions: this.additionalDocumentPullSourceOptions,
		});

		// The column is citext, so match case-insensitively and let setValue normalize the casing.
		const savedAdditionalDocumentSource = _.get(this, 'channel.additionalDocumentPullSource');
		if (!_.isNil(savedAdditionalDocumentSource)) {
			const additionalDocumentOption = this.additionalDocumentPullSourceOptions.find(
				o => o.value.toLowerCase() === String(savedAdditionalDocumentSource).toLowerCase()
			);
			if (additionalDocumentOption) {
				this.additionalDocumentPullSourceDropdownStore.setValue(additionalDocumentOption);
			}
		}
	};

	setSellerCloudDropdownValue = () => {
		if (!_.get(this.channel, 'sellerCloudChannels')) return null;

		let selectedSellerCloudChannelsSet = new Set(this.channel.sellerCloudChannels.split(','));
		if (_.get(this, 'channel.channelType.name') === channelType.sellerCloud) {
			return _.filter(sellerCloudChannels, sellerCloudChannel =>
				selectedSellerCloudChannelsSet.has(String(sellerCloudChannel.value))
			);
		}

		return null;
	};

	getShipmentDocumentPullSourceOptions = () => {
		const currentChannelType = _.get(this.channelTypeDropdownStore, 'value.label');
		// show all options for seller cloud channels
		if (currentChannelType === channelType.sellerCloud) {
			return this.shipmentDocumentPullSourceOptions;
		}
		// exclude SellerCloud optionfor non-seller cloud channels since it's not applicable
		return this.shipmentDocumentPullSourceOptions.filter(o => o.label !== 'SellerCloud');
	};

	// The options depend on the channel type, which on create is only picked after this dropdown is built.
	@action.bound
	refreshShipmentDocumentPullSourceOptions = () => {
		if (!this.shipmentDocumentPullSourceDropdownStore) return;
		const options = this.getShipmentDocumentPullSourceOptions();
		this.shipmentDocumentPullSourceDropdownStore.staticOptions = options;
		this.shipmentDocumentPullSourceDropdownStore.options = options;
		const selected = this.shipmentDocumentPullSourceDropdownStore.value;
		if (selected && !options.some(o => o.value === selected.value)) {
			this.shipmentDocumentPullSourceDropdownStore.clear();
		}
	};

	@action.bound
	onChannelTypeChange = async option => {
		if (option.label === channelType.goFlow && !this.goFlowStoreDropdownStore) {
			await this.initializeGoFlowDropdowns(false);
		} else if (option.label === channelType.sellerCloud) {
			await this.initializeSellerCloudDropdowns(false);
		}
		this.refreshShipmentDocumentPullSourceOptions();
		this.handleBuyShippingCarriers();
		// Channel types in channelTypesWithoutRequiredCarriers are not scoped, so the allowed set changes.
		await this.getInScopeCarrierServices();
	};

	@action.bound
	onChannelTypeInitialize = async () => {
		if (_.get(this.channelTypeDropdownStore, 'value.label') === channelType.goFlow) {
			await this.initializeGoFlowDropdowns();
		} else if (_.get(this.channelTypeDropdownStore, 'value.label') === channelType.sellerCloud) {
			await this.initializeSellerCloudDropdowns();
		}
	};

	@action.bound
	initializeReactions = () => {
		reaction(
			() => this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value,
			() => {
				const rules =
					this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value
						? 'required'
						: '';
				_.forEach([carrierTypes.fedex, carrierTypes.ups], carrierType => {
					if (this.form.has(`${carrierType.toLowerCase()}BuyShippingCarrier`)) {
						this.form.$(`${carrierType.toLowerCase()}BuyShippingCarrier`).set('rules', rules);
						this.form.$(`${carrierType.toLowerCase()}BuyShippingCarrier`).validate();
					}
				});
			}
		);
		reaction(
			() => _.get(this, 'channelTypeDropdownStore.value'),
			() => {
				this.setFieldRules();
				this.form.$('useBuyShippingForPrime').value =
					_.get(this.channelTypeDropdownStore, 'value.label') === channelType.amazon ||
					this.form.$('useAmazonBuyShipping').value;
			}
		);
		reaction(
			() => this.form.$('useAmazonBuyShipping').value,
			() => {
				this.handleBuyShippingCarriers();
			}
		);
	};

	getRequiredCarriers = () => {
		if (!_.get(this.channel, 'carrierChannels')) return null;
		const selectedCarriers = [];
		_.forEach(this.channel.carrierChannels, item => {
			if (item.carrierChannelType == carrierChannelTypeEnum.requiredCarrier) {
				selectedCarriers.push(item.carrier);
			}
		});
		return selectedCarriers;
	};

	@action.bound
	getGoFlowStores = async () => {
		try {
			const { apiKey, subdomain } = this.form.values();
			await this.goFlowStoreDropdownStore.updateFilter({ apiKey, subdomain });
			if (_.get(this.goFlowStoreDropdownStore, 'store.response.item') === null)
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.STORESERROR']);
			else this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.STORESSUCCESS']);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.STORESERROR']);
		}
	};

	@action.bound
	getSellerCloudCompanies = async () => {
		try {
			const { userName, password, teamName } = this.form.values();
			await this.sellerCloudCompanyDropdownStore.updateFilter({ userName, password, teamName });
			if (_.get(this.sellerCloudCompanyDropdownStore, 'store.response.item')) {
				if (this.sellerCloudCompanyDropdownStore.store.response.item.length > 0) {
					this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.COMPANYSUCCESS']);
				} else {
					this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYNORESULTS']);
				}
			} else {
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYERROR']);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.COMPANYERROR']);
		}
	};

	@action.bound
	getSellerCloudSavedViews = async () => {
		try {
			const { userName, password, teamName } = this.form.values();
			await this.sellerCloudSavedViewDropdownStore.updateFilter({ userName, password, teamName });
			if (_.get(this.sellerCloudSavedViewDropdownStore, 'store.response.item')) {
				if (this.sellerCloudSavedViewDropdownStore.store.response.item.length > 0) {
					this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.SAVEDVIEWSUCCESS']);
				} else {
					this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.SAVEDVIEWNORESULTS']);
				}
			} else {
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.SAVEDVIEWERROR']);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.SAVEDVIEWERROR']);
		}
	};

	@action.bound
	validateCredentials = async () => {
		try {
			let data = this.form.values();
			await this.credentialsValidationStore.validateChannel({
				channelTypeName: _.get(this.channelTypeDropdownStore, 'value.label'),
				...data,
			});
			this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.CONNECTIONVALID']);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.CONNECTIONINVALID']);
		}
	};

	@action.bound
	onSellerChange = async () => {
		await Promise.all([this.handleCarriersOnSellerChange(), this.getWarehouses()]);
	};

	@action.bound
	handleCarriersOnSellerChange = async () => {
		await this.clearCarrierFields('BuyShippingCarrier');
		await this.clearCarrierFields(
			'TrackingCarrier',
			this.carrierTypes.filter(ct => ct.abrv !== 'ltl' && ct.abrv !== 'passport' && ct.abrv !== 'ontrac')
		);
		if (this.carrierChannelDropdownStore) {
			this.carrierChannelDropdownStore.clear();
			await this.carrierChannelDropdownStore.updateFilter({
				sellerIds: this.sellerDropdownStore.value ? [this.sellerDropdownStore.value.value] : null,
			});
		}
	};

	@action.bound
	getChannel = async () => {
		const locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		const localeData = moment.localeData();
		this.datePickerFormat = localeData.longDateFormat('L');
		this.channel = await this.listStore.getChannel(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'channelType',
				'seller',
				'timezone',
				'seller',
				'carrier',
				'carrierChannels',
				'carrierChannels.carrier',
				'carrierChannels.carrier.carrierType',
				// Required: createOrUpdateChannel always sends channelExcludedCarrierServices and the API diffs
				// it against the stored rows. Without this embed the selection seeds empty and every channel
				// save would wipe the channel's exclusions.
				'channelExcludedCarrierServices',
				'productInventoryRestockDate',
				'customFieldInputs',
				'customFieldInputs.customFieldInputVariables',
				'customFieldInputs.customFieldInputVariables.customFieldVariable',
			],
		});
		this.selectedItem = this.channel;
		this.excludedCarrierServiceIds = _.map(
			_.get(this.channel, 'channelExcludedCarrierServices'),
			'carrierServiceId'
		);
		if (
			_.get(this, 'channel.productInventoryRestockDate.restockDate') != null &&
			_.get(this, 'channel.productInventoryRestockDate.scheduledRestockDateTime') != null &&
			!isCurrentDateGreater(this.channel.productInventoryRestockDate.restockDate)
		) {
			this.scheduledRestockDate = formatDate(
				this.channel.productInventoryRestockDate.scheduledRestockDateTime,
				true,
				'LLL'
			);
		}
		const { carrierChannels, ...other } = this.listStore.item; // dont update carrierChannels because it breaks multi-select validation
		this.form.updateForm(other, true);
		if (this.channel.channelType.name === channelType.amazonVendor) {
			this.form.$('useSandbox').value = this.channel.useSandbox === 'true'; // Because it comes as string from Json updateForm doesn't work.
		}
		if (this.channel.channelType.name === channelType.goFlow) {
			this.form.$('checkSplit').value = this.channel.checkSplit === 'true';
		}
		if (this.channel.channelType.name === channelType.sellerCloud) {
			this.form.$('filterOrdersToPull').value = this.channel.filterOrdersToPull === 'true';
			this.form.$('flagOrderAsImported').value = this.channel.flagOrderAsImported === 'true';
			this.form.$('flagOrderAsShipped').value = this.channel.flagOrderAsShipped === 'true';
			this.form.$('useCustomShipmentTrackingColumn').value = this.channel.useCustomShipmentTrackingColumn === 'true';
		}
		if (this.channel.lastSync) {
			const localDateTime = moment.utc(this.channel.lastSync).tz(moment.tz.guess());
			this.form.$('lastSyncDate').set('value', localDateTime.format('MM-DD-YYYY'));
			this.form.$('lastSyncTime').set('value', localDateTime.format('HH:mm:ss'));
		}
		this.form.$('useAmazonBuyShipping').value = this.channel.useAmazonBuyShipping;
	};

	onLastSyncChange = () => {
		this.form.$('lastSyncDate').validate({ showErrors: true });
		this.form.$('lastSyncTime').validate({ showErrors: true });
	};

	@action.bound
	initializeSellerCloudDropdowns = async (fetchInitialValues = true) => {
		const { userName, password, teamName } = this.form.values();
		if (!this.sellerCloudCompanyDropdownStore) {
			this.sellerCloudCompanyDropdownStore = new BaasicDropdownViewStore();
			this.sellerCloudCompanyDropdownStore.initialize({
				field: this.form.$('companyIds'),
				value: _.get(this.channel, 'companies') ? JSON.parse(this.channel.companies) : null,
				store: new ChannelStore(this.rootStore),
				fetchFunc: 'getSellerCloudCompanies',
				fetchInitialValues: fetchInitialValues,
				isMulti: true,
				itemTemplate: {
					value: 'id',
					label: 'companyName',
				},
				filterProperties: {
					userName,
					password,
					teamName,
				},
				fetchAll: true,
			});
		}

		if (!this.sellerCloudSavedViewDropdownStore) {
			this.sellerCloudSavedViewDropdownStore = new BaasicDropdownViewStore();
			this.sellerCloudSavedViewDropdownStore.initialize({
				field: this.form.$('savedViewId'),
				store: new ChannelStore(this.rootStore),
				fetchFunc: 'getSellerCloudSavedViews',
				fetchInitialValues: fetchInitialValues,
				itemTemplate: {
					value: 'id',
					label: 'name',
				},
				filterProperties: {
					userName,
					password,
					teamName,
				},
				fetchAll: true,
				defaultInitialValue: { path: 'value', value: _.get(this.channel, 'savedViewId') },
			});
		}
	};

	@action.bound
	initializeGoFlowDropdowns = async (fetchInitialValues = true) => {
		const { apiKey, subdomain } = this.form.values();
		this.goFlowStoreDropdownStore = new BaasicDropdownViewStore();
		await this.goFlowStoreDropdownStore.initialize({
			field: this.form.$('storeIds'),
			value: _.get(this.channel, 'stores') ? JSON.parse(this.channel.stores) : null,
			store: new ChannelStore(this.rootStore),
			fetchFunc: 'getGoFlowStores',
			fetchInitialValues: fetchInitialValues,
			isMulti: true,
			filterProperties: {
				apiKey,
				subdomain,
			},
			fetchAll: true,
		});
	};

	@action.bound
	getWarehouses = async () => {
		this.warehouses = (await this.applicationStore.find({
			rpp: 100,
			page: 1,
			isOrganizationActive: true,
			warehouseOnly: true,
			sellerWarehousesOnlyOrganizationId: this.isSuperAdmin ? _.get(this.sellerDropdownStore, 'value.value') : null,
			includeOnlySellerWarehouses: true,
		})).item;
		await this.prepareData();
	};

	@action.bound
	prepareData = async () => {
		this.convertWarehouses = [];
		this.amazonTemplatesModel = {
			amazonTemplates: [],
			defaultPrimeTemplate: '',
		};
		_.each(this.warehouses, warehouse => {
			this.convertWarehouses.push({
				warehouseName: warehouse.identifier,
				name: null, // Warehouse Code.
				warehouseId: warehouse.id,
			});
		});
	};

	getChannelCredentials = formValues => {
		let channelCredentials = {};

		switch (true) {
			case this.channelTypeDropdownStore.value.label === channelType.goFlow:
				channelCredentials = {
					apiKey: formValues.apiKey,
					subdomain: formValues.subdomain,
					checkSplit: formValues.checkSplit,
				};
				if (this.goFlowStoreDropdownStore.value && this.goFlowStoreDropdownStore.value.length > 0) {
					const stores = this.goFlowStoreDropdownStore.value.map(option => {
						return { id: option.value, name: option.label };
					});
					channelCredentials.stores = JSON.stringify(stores);
				}
				break;

			case this.channelTypeDropdownStore.value.label === channelType.walmart:
				// Json is replaced wholesale on save, so the OAuth credentials have to be sent back
				// or the first save after connecting to Walmart would wipe the refresh token.
				channelCredentials = {
					apiKey: formValues.apiKey,
					apiSecret: formValues.apiSecret,
					refreshToken: formValues.refreshToken,
					walmartSellerId: formValues.walmartSellerId,
					walmartAuthorizedOn: formValues.walmartAuthorizedOn,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.shipStation:
				channelCredentials = {
					apiKey: formValues.apiKey,
					apiSecret: formValues.apiSecret,
					apiKeyV2: formValues.apiKeyV2,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.amazon ||
				this.channelTypeDropdownStore.value.label === channelType.amazonVendor:
				channelCredentials = {
					amazonSellerId: formValues.amazonSellerId,
					marketplaceId: formValues.marketplaceId,
					refreshToken: formValues.refreshToken,
					sellingPartyId: formValues.sellingPartyId,
					useSandbox: formValues.useSandbox,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.wooCommerce:
				channelCredentials = {
					url: formValues.url,
					consumerKey: formValues.consumerKey,
					consumerSecret: formValues.consumerSecret,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.mirakl:
				channelCredentials = {
					url: formValues.url,
					shopId: formValues.shopId,
					apiKey: formValues.apiKey,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.shopify:
				channelCredentials = {
					url: formValues.url,
					accessToken: formValues.accessToken,
					clientId: formValues.clientId,
					clientSecret: formValues.clientSecret,
				};
				break;

			case this.channelTypeDropdownStore.value.label === channelType.sellerCloud:
				channelCredentials = {
					userName: formValues.userName,
					password: formValues.password,
					teamName: formValues.teamName,
					url: formValues.url,
					filterOrdersToPull: formValues.filterOrdersToPull,
					flagOrderAsImported: formValues.flagOrderAsImported,
					flagOrderAsShipped: formValues.flagOrderAsShipped,
					useCustomShipmentTrackingColumn: formValues.useCustomShipmentTrackingColumn,
				};
				if (formValues.filterOrdersToPull) {
					channelCredentials.savedViewId = formValues.savedViewId;
				} else {
					if (this.sellerCloudDropdownStore.value && this.sellerCloudDropdownStore.value.length > 0) {
						channelCredentials.sellerCloudChannels = this.sellerCloudDropdownStore.value
							.map(item => String(item.value))
							.join(',');
					}
					if (this.sellerCloudCompanyDropdownStore.value && this.sellerCloudCompanyDropdownStore.value.length > 0) {
						const companies = this.sellerCloudCompanyDropdownStore.value.map(option => {
							return { id: option.value, companyName: option.label };
						});
						channelCredentials.companies = JSON.stringify(companies);
					}
				}
				break;

			case this.channelTypeDropdownStore.value.label === channelType.costco:
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
				break;

			case [
				channelType.buybuyBaby,
				channelType.mattressFirm,
				channelType.homeDepot,
				channelType.overstock,
				channelType.wayfair,
				channelType.overstockCA,
				channelType.logicBroker,
			].includes(this.channelTypeDropdownStore.value.label):
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
				break;
		}
		const includeAmazonCredentials =
			(isSome(this.channel) && isSome(this.channel.useAmazonBuyShipping)) || this.form.$('useAmazonBuyShipping').value;

		channelCredentials.amazonSellerId = includeAmazonCredentials ? formValues.amazonSellerId : null;
		channelCredentials.marketplaceId = includeAmazonCredentials ? formValues.marketplaceId : null;
		channelCredentials.refreshToken = includeAmazonCredentials ? formValues.refreshToken : null;

		if (this.shipmentDocumentPullSourceDropdownStore && this.shipmentDocumentPullSourceDropdownStore.value) {
			channelCredentials.banyanClientId = formValues.banyanClientId;
			channelCredentials.banyanClientSecret = formValues.banyanClientSecret;
		}

		return channelCredentials;
	};

	getTrackingCarrierChannels = (formValues, carrierChannels) => {
		const trackingCarrierFields = _.filter(Object.getOwnPropertyNames(formValues), item => {
			return item.includes('TrackingCarrier');
		});
		_.forEach(trackingCarrierFields, fieldKey => {
			const trackingCarrierDropdown = this.trackingCarrierDropdowns.find(d => d.field.key === fieldKey);
			if (_.get(trackingCarrierDropdown, 'value')) {
				carrierChannels.push({
					carrierId: formValues[fieldKey],
					carrierChannelType: carrierChannelTypeEnum.trackingCarrier,
				});
			}
		});
	};

	getBuyShippingCarrierChannels = (formValues, carrierChannels) => {
		const buyShippingFields = _.filter(Object.getOwnPropertyNames(formValues), item => {
			return item.includes('BuyShippingCarrier');
		});
		_.forEach(buyShippingFields, fieldKey => {
			const buyShippingDropdown = this.buyShippingDropdowns.find(d => d.field.key === fieldKey);
			if (_.get(buyShippingDropdown, 'value') && buyShippingDropdown.value.label !== 'None') {
				carrierChannels.push({
					carrierId: formValues[fieldKey],
					carrierChannelType: carrierChannelTypeEnum.buyShipping,
				});
			}
		});
	};

	getRequiredCarrierChannels = (formValues, carrierChannels) => {
		if (!formValues.carrierChannelIds || formValues.carrierChannelIds.length === 0) return;

		_.map(formValues.carrierChannelIds, item => {
			carrierChannels.push({
				carrierId: item,
				carrierChannelType: carrierChannelTypeEnum.requiredCarrier,
			});
		});
	};

	getCarrierChannels = formValues => {
		const channelTypeLabel = this.channelTypeDropdownStore.value.label;
		let carrierChannels = [];
		if (channelTypeLabel !== channelType.shipStation) {
			this.getTrackingCarrierChannels(formValues, carrierChannels);
		}
		if (!channelTypesWithoutRequiredCarriers.includes(channelTypeLabel)) {
			this.getRequiredCarrierChannels(formValues, carrierChannels);
		}
		this.getBuyShippingCarrierChannels(formValues, carrierChannels);

		return carrierChannels;
	};

	// Always returns an array. The API diffs it against the stored rows: an empty array clears every
	// exclusion, omitting the property leaves them untouched.
	getChannelExcludedCarrierServices = () => {
		return _.map(this.excludedCarrierServiceIds, carrierServiceId => ({ carrierServiceId }));
	};

	@action.bound
	setExcludedCarrierServiceIds = excludedCarrierServiceIds => {
		this.excludedCarrierServiceIds = excludedCarrierServiceIds;
	};

	// Channel types in channelTypesWithoutRequiredCarriers hide the required carriers dropdown entirely, so
	// there is nothing to scope by - those channels keep the full carrier service list.
	@computed get scopesCarrierServicesByRequiredCarriers() {
		return !channelTypesWithoutRequiredCarriers.includes(_.get(this.channelTypeDropdownStore, 'value.label'));
	}

	// A carrier's carrierTypeId is a Guid while a carrier service's is an SGuid, so the two flat ids never
	// compare equal. Both sides embed carrierType, whose id is an SGuid on either side - join on that.
	@computed get requiredCarrierTypeIds() {
		return _.uniq(
			_.compact(
				_.map(_.get(this, 'carrierChannelDropdownStore.value'), option => _.get(option, 'extra.carrierType.id'))
			)
		);
	}

	@computed get isShipStationChannel() {
		return _.get(this.channelTypeDropdownStore, 'value.label') === channelType.shipStation;
	}

	// The carrier types the carrier service fetch is scoped to (none means no scoping), plus whether the channel
	// is left with nothing in scope at all. The header count and the modal both build their filter from this, so
	// the two always list the same set; isCarrierServiceInScope then applies what a fetch filter cannot express.
	// Not a computed - it reads carrierTypes, which is not observable (it is loaded once, before this is ever
	// read), so a computed would cache whatever it saw first.
	get carrierServiceScope() {
		// A ShipStation channel has no required carriers to scope by, but ShipStation quotes only the FedEx, UPS
		// and USPS accounts connected to it, so no other carrier type can ever be used on such a channel.
		if (this.isShipStationChannel) {
			return { carrierTypeIds: this.shipStationCarrierTypeIds, hasNoRequiredCarriers: false };
		}
		if (!this.scopesCarrierServicesByRequiredCarriers) {
			return { carrierTypeIds: [], hasNoRequiredCarriers: false };
		}
		return {
			carrierTypeIds: this.requiredCarrierTypeIds,
			hasNoRequiredCarriers: this.requiredCarrierTypeIds.length === 0,
		};
	}

	get shipStationCarrierTypeIds() {
		return _.compact(_.map(shipStationCarrierTypes, name => _.get(_.find(this.carrierTypes, { name }), 'id')));
	}

	// Narrows fetched carrier services to the ones the channel can actually use. The scope's carrier type ids
	// only narrow the fetch, so this stays the authority on what ends up listed.
	isCarrierServiceInScope = carrierService => !this.isShipStationChannel || isShipStationCarrierService(carrierService);

	@computed get canEditExcludedCarrierServices() {
		return !this.scopesCarrierServicesByRequiredCarriers || this.requiredCarrierTypeIds.length > 0;
	}

	// Shown on the header button. Counts allowed rather than excluded services so it lines up with the
	// modal, which is titled "Allowed Carrier Services".
	@computed get allowedCarrierServiceCount() {
		return _.difference(this.inScopeCarrierServiceIds, _.map(this.excludedCarrierServiceIds, id => String(id))).length;
	}

	/**
	 * Saves the channel. Returns the channel the API created on insert, so the Walmart connect
	 * prompt can pick up its id, and null when the save did not go through.
	 */
	@action.bound
	createOrUpdateChannel = async () => {
		this.suspend();
		let createdChannel = null;
		try {
			if (!this.isEdit && !this.customLabel) {
				// Custom Label data on insert is required
				this.rootStore.notificationStore.error('MESSAGES.CHANNEL.MISSINGREQUIREDVALUESLABEL');
				this.resume();
				return null;
			}

			const formValues = this.form.values();
			const shipmentDocumentPullSource = (() => {
				const opt = this.shipmentDocumentPullSourceOptions.find(o => o.value === formValues.shipmentDocumentPullSource);
				return opt ? opt.label : formValues.shipmentDocumentPullSource;
			})();
			// Only SellerCloud channels show the dropdown, so drop a value left over from a channel type switch.
			const additionalDocumentPullSource =
				this.channelTypeDropdownStore.value.label === channelType.sellerCloud
					? formValues.additionalDocumentPullSource || null
					: null;
			const carrierChannels = this.getCarrierChannels(formValues);
			const channelExcludedCarrierServices = this.getChannelExcludedCarrierServices();
			const channelCredentials = this.getChannelCredentials(formValues);
			const customLabelValues = this.customLabel
				? { ...this.customLabel }
				: {
						sellerName: this.channel.sellerName,
						sellerPhone: this.channel.sellerPhone,
						useCustomReturnAddress: this.channel.useCustomReturnAddress,
						returnName: this.channel.returnName,
						returnPhone: this.channel.returnPhone,
						returnAddress: this.channel.returnAddress,
						returnCity: this.channel.returnCity,
						returnZip: this.channel.returnZip,
						returnState: this.channel.returnState,
						returnStateName: this.channel.returnStateName,
				  };

			if (
				this.channelTypeDropdownStore.value.label === channelType.sellerCloud &&
				(!channelCredentials.url || channelCredentials.teamName !== this.channel.teamName)
			) {
				try {
					channelCredentials.url = await this.listStore.getSellerCloudUrl(channelCredentials.teamName);
				} catch (error) {
					this.rootStore.notificationStore.error('MESSAGES.CHANNEL.MISSINGSELLERCLOUDURL');
					this.resume();
					return null;
				}
			}
			// Rebuilt from scratch on every save, so anything stored in customAttributes has to be
			// listed here or saving the channel silently drops it.
			const customAttributes = {
				logicBrokerExcludeOrderParameters:
					formValues.logicBrokerExcludeOrderParameters != ''
						? formValues.logicBrokerExcludeOrderParameters.filter(attr => attr.key != '' && attr.value != '')
						: null,
				useShippingApiV2: formValues.useShippingApiV2,
				walmartUseSandbox: formValues.walmartUseSandbox,
			};

			if (
				this.showDuplicateExludeOrderParameterKeyError ||
				this.showEmptyExcludeOrderParameterValueError ||
				this.showEmptyExcludeOrderParameterKeyError
			) {
				this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.INVALIDEXCLUDEORDERPARAMETERS']);
				this.resume();
				return null;
			}

			if (this.isEdit) {
				await this.listStore.updateChannel(
					{
						id: this.rootStore.routerStore.routerState.params.id,
						name: formValues.name,
						timezoneId: formValues.timezoneId,
						channelTypeId: formValues.channelTypeId,
						carrierChannels: carrierChannels,
						channelExcludedCarrierServices: channelExcludedCarrierServices,
						printAdditionalDocs: this.channel.printAdditionalDocs,
						shouldSync: this.channel.shouldSync,
						onlyShipFromRequiredWarehouse: this.channel.onlyShipFromRequiredWarehouse,
						doNotShipAsap: formValues.doNotShipAsap,
						delayLabelPull: formValues.delayLabelPull,
						raiseUnlinkedSkuWarning: formValues.raiseUnlinkedSkuWarning,
						onlyShipUsingRequiredCarrierService: this.channel.onlyShipUsingRequiredCarrierService,
						mapChannelService: this.channel.mapChannelService,
						updateInventory: this.channel.updateInventory,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						lastSync:
							!formValues.filterOrdersToPull && formValues.lastSyncDate && formValues.lastSyncTime
								? convertTimeToUTC(`${formValues.lastSyncDate} ${formValues.lastSyncTime}`)
								: null,
						checkBuyShippingSunday:
							this.channel.channelType.name === channelType.amazon ? formValues.checkBuyShippingSunday : false,
						sellerId: this.channel.sellerId,
						...channelCredentials,
						...customLabelValues,
						useAmazonBuyShipping: formValues.useAmazonBuyShipping,
						shipmentDocumentPullSource: shipmentDocumentPullSource,
						additionalDocumentPullSource: additionalDocumentPullSource,
						customAttributes: isSome(customAttributes) ? JSON.stringify(customAttributes) : null,
					},
					sourceOfChange.updateChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: this.form.$('name').value }]);
			} else {
				createdChannel = await this.listStore.createChannel(
					{
						name: formValues.name,
						channelTypeId: formValues.channelTypeId,
						carrierChannels: carrierChannels,
						channelExcludedCarrierServices: channelExcludedCarrierServices,
						convertWarehouses: this.convertWarehouses,
						timezoneId: formValues.timezoneId,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						doNotShipAsap: formValues.doNotShipAsap,
						delayLabelPull: formValues.delayLabelPull,
						raiseUnlinkedSkuWarning: formValues.raiseUnlinkedSkuWarning,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						checkBuyShippingSunday:
							this.channelTypeDropdownStore.value.label === channelType.amazon
								? formValues.checkBuyShippingSunday
								: false,
						sellerId: this.isSuperAdmin ? formValues.sellerId : null,
						sellerName: formValues.sellerName,
						sellerPhone: formValues.sellerPhone,
						defaultPrimeTemplate:
							this.channelTypeDropdownStore.value.label === channelType.amazon ? formValues.defaultPrimeTemplate : null,
						...channelCredentials,
						...customLabelValues,
						...this.amazonTemplatesModel,
						useAmazonBuyShipping: formValues.useAmazonBuyShipping,
						shipmentDocumentPullSource: shipmentDocumentPullSource,
						additionalDocumentPullSource: additionalDocumentPullSource,
						customAttributes: isSome(customAttributes) ? JSON.stringify(customAttributes) : null,
					},
					sourceOfChange.createChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.CHANNEL', { name: this.form.$('name').value }]);
			}
			this.rootStore.routerStore.goTo('master.app.main.channel.list');
			// Asked from the list, after the transition: the confirm modal is held by the root view
			// store and rendered by the header, so it outlives this page, and dismissing it in any
			// way then leaves the channel saved with the user somewhere sane.
			if (isSome(createdChannel) && this.canConnectWalmartOnCreate) {
				this.openConfirmationModal(this.getWalmartConnectConfirmModalProps(createdChannel.id));
			}
			if (
				this.isEdit &&
				this.channelTypeDropdownStore.value.label === channelType.amazon &&
				this.form.$('useAmazonMli').value == false &&
				this.form.$('useAmazonMli').value != this.channel.useAmazonMli
			) {
				const notificationId = this.rootStore.notificationStore.load(
					translate(['CHANNEL.STARTEDAMAZONMLIRESET', { name: this.channel.name }])
				);
				const result = await new UpdateInventoryStore(this.rootStore).updateInventoryForChannel(
					this.channel.id,
					sourceOfChange.channelEditUpdateInventoryLog,
					true
				);
				this.rootStore.notificationStore.dismiss(notificationId);
				if (result) {
					this.rootStore.notificationStore.success(['CHANNEL.MLIINVENTORYRESETSUCCESS', { name: this.channel.name }]);
				} else {
					this.rootStore.notificationStore.error(['CHANNEL.MLIINVENTORYRESETFAILED', { name: this.channel.name }]);
				}
			}
		} catch (err) {
			if (err.statusCode === 500) {
				if (err.data.details === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
					const errorMessage = translate(['MESSAGES.CHANNEL.DUPLICATECREDENTIALS']);
					this.rootStore.notificationStore.error(errorMessage);
				} else {
					// Without this the save fails silently, which reads as a dead Save button.
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			} else {
				if (err.statusCode === 400) {
					err.data.errorMessage = translate('MESSAGES.CHANNEL.EXISTS');
					if (err.data.exceptionMessage === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
						err.data.errorMessage = translate('MESSAGES.CHANNEL.DUPLICATECREDENTIALS');
					}
				}
				this.rootStore.notificationStore.error(err.data.errorMessage);
			}
		}
		this.resume();
		return createdChannel;
	};

	@action.bound
	async getCarrierTypes() {
		await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName.carrierType);
		this.carrierTypes = this.lookupStore.data;
	}

	// Only the ids are needed here - the modal fetches the services themselves when it opens. carrierType is
	// embedded because isCarrierServiceInScope reads it.
	@action.bound
	async getInScopeCarrierServices() {
		const { carrierTypeIds, hasNoRequiredCarriers } = this.carrierServiceScope;
		if (hasNoRequiredCarriers) {
			this.inScopeCarrierServiceIds = [];
			return;
		}
		const filter = { page: 1, rpp: 500, embed: ['carrierType'] };
		if (carrierTypeIds.length > 0) {
			filter.carrierTypeIds = carrierTypeIds;
		}
		await this.carrierServiceStore.find(filter);
		this.inScopeCarrierServiceIds = _.map(
			_.filter(this.carrierServiceStore.data, carrierService => this.isCarrierServiceInScope(carrierService)),
			carrierService => String(carrierService.id)
		);
	}

	// An exclusion only means anything while the channel requires a carrier of that carrier type, so the
	// exclusions are reconciled against the scope on both sides of the change: a carrier type that just left
	// takes its exclusions with it, and one that just arrived starts out fully allowed.
	@action.bound
	async onRequiredCarriersChange() {
		const previouslyInScopeIds = this.inScopeCarrierServiceIds;
		await this.getInScopeCarrierServices();
		const stillInScopeIds = _.intersection(previouslyInScopeIds, this.inScopeCarrierServiceIds);
		this.excludedCarrierServiceIds = _.filter(this.excludedCarrierServiceIds, id =>
			_.includes(stillInScopeIds, String(id))
		);
	}

	@action.bound
	setSellingPartnerAPIRules() {
		this.form.$('amazonSellerId').set('rules', 'required');
		this.form.$('marketplaceId').set('rules', 'required');
		this.form.$('refreshToken').set('rules', 'required');
	}

	@action.bound
	handleBuyShippingCarriers = () => {
		if (
			_.get(this, 'channelTypeDropdownStore.value.label') === channelType.amazon ||
			this.form.$('useAmazonBuyShipping').value == true
		) {
			this.addBuyShippingCarrierFields();
			this.removeBuyShippingCarrier();
			if (this.isEdit) {
				const carrierChannels = _.filter(this.channel.carrierChannels, item => {
					return item.carrierChannelType == carrierChannelTypeEnum.buyShipping;
				});
				this.updateCarrierFields(carrierChannels, 'BuyShippingCarrier');
				_.forEach([carrierTypes.fedex, carrierTypes.ups], carrierType => {
					const dropdown = this.buyShippingDropdowns.find(
						d => d.field.name === `${carrierType.toLowerCase()}BuyShippingCarrier`
					);
					if (dropdown && !dropdown.value) {
						dropdown.setValue({ name: 'None' });
					}
				});
			} else {
				const carrierChannels = _.filter(this.carrierChannelDropdownStore.value, item => {
					return item.carrierChannelType == carrierChannelTypeEnum.buyShipping;
				});
				this.updateCarrierFields(carrierChannels, 'BuyShippingCarrier');
				_.forEach([carrierTypes.fedex, carrierTypes.ups], carrierType => {
					const dropdown = this.buyShippingDropdowns.find(
						d => d.field.name === `${carrierType.toLowerCase()}BuyShippingCarrier`
					);
					if (dropdown && !dropdown.value) {
						dropdown.setValue({ name: 'None' });
					}
				});
			}
		} else {
			this.removeBuyShippingCarrierFields();
		}
	};

	@action.bound
	setFieldRules() {
		if (!this.channelTypeDropdownStore.value) return;
		this.resetFieldRules();
		if ([channelType.amazonVendor, channelType.amazon].includes(this.channelTypeDropdownStore.value.label)) {
			this.setSellingPartnerAPIRules();
		}

		if (this.channelTypeDropdownStore.value.label === channelType.goFlow) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('subdomain').set('rules', 'required');
			this.form.$('storeIds').set('rules', 'required');
		}

		if (this.channelTypeDropdownStore.value.label === channelType.amazonVendor) {
			this.form.$('sellingPartyId').set('rules', 'required');
		} else {
			this.form.$('sellingPartyId').set('rules', '');
		}

		// Kept out of resetFieldRules so the rule always matches whether the dropdown is rendered at all.
		const hasRequiredCarriers = !channelTypesWithoutRequiredCarriers.includes(
			this.channelTypeDropdownStore.value.label
		);
		this.form.$('carrierChannelIds').set('rules', hasRequiredCarriers ? 'required' : '');

		if (
			[channelType.costco, channelType.overstockCA, channelType.logicBroker].includes(
				this.channelTypeDropdownStore.value.label
			)
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.overstockCA) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.mirakl) {
			this.form.$('url').set('rules', 'required');
			this.form.$('shopId').set('rules', 'required');
			this.form.$('apiKey').set('rules', 'required');
		}
		if (this.channelTypeDropdownStore.value.label === channelType.shopify) {
			this.form.$('url').set('rules', 'required');
			if (this.form.$('accessToken').value) {
				this.form.$('accessToken').set('rules', 'required');
			} else {
				this.form.$('clientId').set('rules', 'required');
				this.form.$('clientSecret').set('rules', 'required');
			}
		}
		if (this.channelTypeDropdownStore.value.label === channelType.sellerCloud) {
			this.form.$('teamName').set('rules', 'required');
			this.form.$('userName').set('rules', 'required');
			this.form.$('password').set('rules', 'required');
			if (this.form.$('filterOrdersToPull').value) {
				this.form.$('savedViewId').set('rules', 'required');
			}
		}
		if (
			[
				channelType.buybuyBaby,
				channelType.mattressFirm,
				channelType.homeDepot,
				channelType.overstock,
				channelType.wayfair,
			].includes(this.channelTypeDropdownStore.value.label)
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		// Walmart deliberately requires nothing: an OAuth connected channel authenticates with our
		// solution provider credentials plus the stored refresh token, and a channel being created
		// has to be saveable before it can be connected at all.
		if (this.channelTypeDropdownStore.value.label === channelType.shipStation) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('apiSecret').set('rules', 'required');
		}
	}

	@action.bound
	onFilterOrdersToPullChange = () => {
		this.setFieldRules();
	};

	@action.bound
	resetFieldRules() {
		this.form.$('apiKey').set('rules', '');
		this.form.$('sellingPartyId').set('rules', '');
		this.form.$('partnerId').set('rules', '');
		this.form.$('apiSecret').set('rules', '');
		this.form.$('refreshToken').set('rules', '');
		this.form.$('accessToken').set('rules', '');
		this.form.$('clientId').set('rules', '');
		this.form.$('clientSecret').set('rules', '');
		this.form.$('amazonSellerId').set('rules', '');
		this.form.$('marketplaceId').set('rules', '');
		this.form.$('url').set('rules', '');
		this.form.$('shopId').set('rules', '');
		this.form.$('teamName').set('rules', '');
		this.form.$('userName').set('rules', '');
		this.form.$('password').set('rules', '');
		this.form.$('savedViewId').set('rules', '');
	}

	@action.bound
	addBuyShippingCarrierFields = () => {
		if (this.buyShippingDropdowns.length === 0) {
			_.each(this.carrierTypes, carrierType => {
				const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
				this.form.add({
					name: fieldName,
					rules:
						[carrierTypes.fedex, carrierTypes.ups].includes(carrierType.name) &&
						(this.form.$('useBuyShippingForPrime').value || this.form.$('useBuyShippingForNonPrime').value)
							? 'required'
							: '',
					label: `CHANNEL.BUYSHIPPING.${carrierType.abrv.toUpperCase()}`,
				});
				const dropdownStore = new BaasicDropdownViewStore({
					field: this.form.$(fieldName),
					store: new CarrierStore(this.rootStore),
					filterProperties: {
						sellerIds: _.get(this.sellerDropdownStore, 'value') ? [this.sellerDropdownStore.value.value] : null,
						carrierTypeIds: [carrierType.id],
						embed: ['carrierType'],
					},
					filterCallback: options => {
						return this.filterBuyShippingCarriers(options, carrierType);
					},
				});
				this.buyShippingDropdowns.push(dropdownStore);
			});
		}
	};

	@action.bound
	filterBuyShippingCarriers = (options, carrierType) => {
		let filteredOptions = options.filter(option =>
			(this.carrierChannelDropdownStore.value ? this.carrierChannelDropdownStore.value : []).some(
				reqCarrier => reqCarrier.value === option.id
			)
		);
		if (carrierType.name === carrierTypes.ups || carrierType.name === carrierTypes.fedex) {
			filteredOptions.push({ name: 'None' });
		}
		return _.map(filteredOptions, option => {
			return {
				value: _.get(option, 'id'),
				label: _.get(option, 'name'),
				extra: option,
			};
		});
	};

	@action.bound
	updateCarrierFields = (carrierChannels, dropdownType) => {
		_.forEach(carrierChannels, carrierChannel => {
			const fieldName = `${carrierChannel.carrier.carrierType.abrv.toLowerCase()}${dropdownType}`;
			const dropdown =
				dropdownType === 'BuyShippingCarrier'
					? this.buyShippingDropdowns.find(d => d.field.name === fieldName)
					: this.trackingCarrierDropdowns.find(d => d.field.name === fieldName);
			if (dropdown) {
				dropdown.setValue(carrierChannel.carrier);
			}
		});
	};

	@action.bound
	clearCarrierFields = async (dropdownType, carrierTypes = null) => {
		_.forEach(carrierTypes || this.carrierTypes, async carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}${dropdownType}`;
			const dropdown =
				dropdownType === 'BuyShippingCarrier'
					? this.buyShippingDropdowns.find(d => d.field.name === fieldName)
					: this.trackingCarrierDropdowns.find(d => d.field.name === fieldName);

			if (!dropdown) return;

			dropdown.clear();
			if (dropdownType === 'TrackingCarrier') {
				await dropdown.updateFilter({
					sellerIds: this.sellerDropdownStore.value ? [this.sellerDropdownStore.value.value] : null,
				});
			}
		});
	};

	@action.bound
	removeBuyShippingCarrierFields = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			const index = this.buyShippingDropdowns.findIndex(d => d.field.name === fieldName);
			if (this.form.has(fieldName) && index !== -1) {
				this.form.del(fieldName);
				this.buyShippingDropdowns.splice(index, 1);
			}
		});
	};

	@action.bound
	removeBuyShippingCarrier = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (!this.form.has(fieldName) || !this.carrierChannelDropdownStore) return;
			const dropdown = this.buyShippingDropdowns.find(d => d.field.name === fieldName);
			const isRequiredCarrierSelected =
				this.carrierChannelDropdownStore.value &&
				this.carrierChannelDropdownStore.value.some(item => item.label === _.get(dropdown, 'value.label'));
			if (!isRequiredCarrierSelected && dropdown.value && dropdown.value.label !== 'None') {
				dropdown.clear();
			}
		});
	};

	@action.bound
	onAuthorizeShopify = async () => {
		try {
			const uniqueIdentifier = Date.now().toString();
			const url = getShopifyOAuthUrl(this.form.$('url').value);
			window.open(url, '_blank', 'noopener,noreferrer');
			await this.listStore.authorizeShopify(uniqueIdentifier);
			this.openConfirmationModal(this.getAuthorizeConfirmModalProps());
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	async onAuthorizeAmazon() {
		try {
			await this.listStore.authorizeAmazon(this.oAuthDate);
			this.openConfirmationModal(this.getAuthorizeConfirmModalProps());
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	/**
	 * True when this Walmart channel already holds an OAuth refresh token. Note isSome() only
	 * rejects null/undefined and an unset mobx-react-form field is '', so the value is tested.
	 */
	@computed
	get isWalmartOAuthConnected() {
		const refreshToken = this.form.$('refreshToken').value;
		return typeof refreshToken === 'string' && refreshToken.trim().length > 0;
	}

	/**
	 * The authorization date in the viewer's own time zone, or null when the channel is not
	 * connected. The value carries a UTC offset, so it is normalized rather than read as written.
	 */
	@computed
	get walmartAuthorizedOnDisplay() {
		const authorizedOn = this.form.$('walmartAuthorizedOn').value;
		if (typeof authorizedOn !== 'string' || authorizedOn.trim().length === 0) {
			return null;
		}
		return formatDate(authorizedOn, true, 'LLL');
	}

	@action.bound
	async onAuthorizeWalmart() {
		// OAuth needs a persisted channel id, because the callback writes the token server side. A
		// channel being created is offered the flow by getWalmartConnectConfirmModalProps instead.
		if (!this.isEdit || !isSome(this.channel)) {
			this.rootStore.notificationStore.error(['OAUTH.WALMART.SAVECHANNELFIRST']);
			return;
		}

		// Opened empty while still inside the click handler; a window.open issued after the await
		// has lost the user gesture and gets blocked as a popup.
		const authorizeTab = window.open('', '_blank');

		this.suspend();
		try {
			const authorizeUrl = await this.getWalmartAuthorizeUrl(this.channel.id);

			if (isSome(authorizeUrl)) {
				if (isSome(authorizeTab)) {
					authorizeTab.opener = null;
					// replace, so back in the new tab does not land on the blank placeholder.
					authorizeTab.location.replace(authorizeUrl);
				} else {
					window.open(authorizeUrl, '_blank', 'noopener');
				}
			} else {
				this.closeAuthorizeTab(authorizeTab);
				this.rootStore.notificationStore.error(['OAUTH.WALMART.AUTHORIZEFAILED']);
			}
		} catch (err) {
			this.closeAuthorizeTab(authorizeTab);
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	/**
	 * True when a channel that was just created can be offered the Walmart connect flow.
	 */
	@computed
	get canConnectWalmartOnCreate() {
		return (
			!this.isEdit &&
			_.get(this.channelTypeDropdownStore, 'value.label') === channelType.walmart &&
			!this.isWalmartOAuthConnected
		);
	}

	/**
	 * Offers the consent flow right after the channel is created. Confirming navigates this tab
	 * into Walmart rather than opening the one the edit page opens: the create page is already gone
	 * by then, so there is nothing unsaved to keep alive, and the callback comes back to the saved
	 * channel. Declining leaves the channel saved and unauthorized.
	 */
	getWalmartConnectConfirmModalProps = channelId => {
		return {
			message: ['CHANNEL.WALMARTCONNECTPROMPT'],
			onConfirm: async () => {
				try {
					const authorizeUrl = await this.getWalmartAuthorizeUrl(channelId);
					if (isSome(authorizeUrl)) {
						window.location.assign(authorizeUrl);
						return;
					}
					this.rootStore.notificationStore.error(['OAUTH.WALMART.AUTHORIZEFAILED']);
				} catch (err) {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			},
		};
	};

	/**
	 * Arms an authorization for the given channel and returns the authorize URL to send a browsing
	 * context to, or null when the server did not hand one back.
	 */
	getWalmartAuthorizeUrl = async channelId => {
		const handoff = walmartHandoff.read();
		const result = await this.listStore.authorizeWalmart({
			channelId: channelId,
			clientType: isSome(handoff) ? handoff.clientType : null,
			authorizeUri: isSome(handoff) ? handoff.walmartCallbackUri : null,
		});
		return isSome(result) && isSome(result.authorizeUrl) ? result.authorizeUrl : null;
	};

	closeAuthorizeTab = authorizeTab => {
		if (!isSome(authorizeTab)) {
			return;
		}
		try {
			authorizeTab.close();
		} catch (err) {
			// Nothing to do; the user is left with a blank tab they can close.
		}
	};

	/**
	 * True when the last sync failed because Walmart rejected the stored authorization. Walmart
	 * sends no notification when a seller revokes access in Seller Center, so a failing token call
	 * is the only signal, and it cannot be told apart from the one year expiry or a transient
	 * error. The credentials are therefore kept and only flagged.
	 */
	@computed
	get walmartNeedsReauthorization() {
		return (
			isSome(this.channel) && this.channel.lastSyncError === 'CHANNELSYNCERROR.WALMART.REAUTHORIZATIONREQUIRED'
		);
	}

	getWalmartDisconnectConfirmModalProps = () => {
		return {
			onConfirm: async () => await this.disconnectWalmart(),
			message: ['CHANNEL.WALMARTDISCONNECTCONFIRM', { sellerId: this.form.$('walmartSellerId').value }],
		};
	};

	@action.bound
	onDisconnectWalmart() {
		this.openConfirmationModal(this.getWalmartDisconnectConfirmModalProps());
	}

	@action.bound
	async disconnectWalmart() {
		this.suspend();
		try {
			await this.listStore.disconnectWalmart({ channelId: this.channel.id });
			// getChannelCredentials sends these straight from the form on every save, and the keys
			// are absent from the reloaded channel so updateForm would not overwrite them. Without
			// clearing them here the next save writes the old refresh token back.
			this.form.$('refreshToken').set('value', '');
			this.form.$('walmartSellerId').set('value', '');
			this.form.$('walmartAuthorizedOn').set('value', '');
			this.rootStore.notificationStore.success(['CHANNEL.WALMARTDISCONNECTSUCCESS']);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	getAuthorizeConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				// Get authorization results from Redis.
				try {
					var response = await this.listStore.getAuthorizationResultAmazon(this.amazonOAuthStateHash);
					if (isSome(response) && isSome(response.refreshToken) && isSome(response.sellerId)) {
						this.form.$('refreshToken').value = response.refreshToken;
						this.form.$('amazonSellerId').value = response.sellerId;
						this.rootStore.notificationStore.success(['OAUTH.AMAZON.SUCCESSFORMUPDATE']);
					} else {
						this.rootStore.notificationStore.error(['OAUTH.AMAZON.FAILGETREFRESHTOKEN']);
					}
				} catch (err) {
					if (err.statusCode === 404) {
						this.rootStore.notificationStore.error(['OAUTH.AMAZON.STATENOTFOUND']);
					} else {
						notificationError({ store: this.rootStore.notificationStore, err });
					}
				}
			},
			message: ['OAUTH.AMAZON.CONFIRMSUCCESSAUTH'],
		};
	};

	openEditRestockDateModal = () => {
		this.openModal({
			onSuccess: async () => await this.initialize(),
			data: this,
			component: RestockDate,
		});
	};

	openEditChannelWarehouseCodesModal = () => {
		if (this.isSuperAdmin && !this.sellerDropdownStore.value) {
			this.rootStore.notificationStore.warning(['CHANNEL.ORGANIZATIONREQUIRED']);
			return;
		}
		this.openModal({
			data: this,
			component: WarehouseCodes,
			className: 'ReactModal--lrg ReactModal--vertical',
		});
	};

	openExcludedCarrierServicesModal = () => {
		// No seller guard - carrier services are a global lookup, not seller scoped.
		this.openModal({
			data: this,
			component: ChannelExcludedCarrierServices,
			className: 'ReactModal--lrg ReactModal--vertical',
		});
	};

	openEditChannelAmazonTemplatesModal = () => {
		if (this.isSuperAdmin && _.get(this.sellerDropdownStore, 'value.value') == null) {
			this.rootStore.notificationStore.warning(['CHANNEL.ORGANIZATIONREQUIRED']);
			return;
		}

		const { amazonSellerId, marketplaceId, refreshToken } = this.form.values();

		if (!amazonSellerId || !marketplaceId || !refreshToken) {
			this.rootStore.notificationStore.error(['AMAZONTEMPLATES.MISSINGCREDENTIALS']);
			return;
		}

		this.openModal({
			data: this,
			component: AmazonTemplates,
			className: 'ReactModal--lrg ReactModal--vertical',
		});
	};

	openCustomLabelModal = () => {
		this.openModal({
			data: this,
			component: CustomLabel,
			className: 'ReactModal--lrg-inter',
		});
	};

	openUpdateInventoryLogModal = () => {
		this.openModal({
			data: this,
			component: UpdateInventoryLog,
			className: 'ReactModal--lrg',
		});
	};

	addLogicBrokerExcludeOrderParameter = () => {
		this.form.$('logicBrokerExcludeOrderParameters').add([{ key: '', value: '' }]);
	};

	@action.bound
	initializeCustomAttributes = async () => {
		const customAttributes = _.get(this.channel, 'customAttributes')
			? JSON.parse(_.get(this.channel, 'customAttributes'))
			: {};
		if (_.get(customAttributes, 'logicBrokerExcludeOrderParameters')) {
			this.form.$('logicBrokerExcludeOrderParameters').add([...customAttributes.logicBrokerExcludeOrderParameters]);
		}
		this.form.$('useShippingApiV2').value = _.get(customAttributes, 'useShippingApiV2') === true;
		this.form.$('walmartUseSandbox').value = _.get(customAttributes, 'walmartUseSandbox') === true;
	};

	@action.bound
	checkForDuplicateExcludeOrderParameterKeys = () => {
		const excludeOrderParameters = this.form.$('logicBrokerExcludeOrderParameters').value;
		if (excludeOrderParameters == '') {
			this.showDuplicateExludeOrderParameterKeyError = false;
			return;
		}
		const keys = excludeOrderParameters.map(param => (param.key || '').trim().toLowerCase()).filter(key => key !== '');
		const hasDuplicates = keys.some((key, index) => keys.indexOf(key) !== index);
		if (hasDuplicates) {
			this.showDuplicateExludeOrderParameterKeyError = true;
		} else {
			this.showDuplicateExludeOrderParameterKeyError = false;
		}
	};

	@action.bound
	checkForEmptyExcludeOrderParameterValues = () => {
		const excludeOrderParameters = this.form.$('logicBrokerExcludeOrderParameters').value;
		if (excludeOrderParameters == '') {
			this.showEmptyExcludeOrderParameterValueError = false;
			return;
		}
		const hasEmptyValues = excludeOrderParameters.some(param => !param.value || !param.value.trim());
		if (hasEmptyValues) {
			this.showEmptyExcludeOrderParameterValueError = true;
		} else {
			this.showEmptyExcludeOrderParameterValueError = false;
		}
	};

	@action.bound
	checkForEmptyExcludeOrderParameterKeys = () => {
		const excludeOrderParameters = this.form.$('logicBrokerExcludeOrderParameters').value;
		if (excludeOrderParameters == '') {
			this.showEmptyExcludeOrderParameterKeyError = false;
			return;
		}
		const hasEmptyKeys = excludeOrderParameters.some(param => !param.key || !param.key.trim());
		if (hasEmptyKeys) {
			this.showEmptyExcludeOrderParameterKeyError = true;
		} else {
			this.showEmptyExcludeOrderParameterKeyError = false;
		}
	};

	@action.bound
	validateExcludeOrderParameters = () => {
		this.checkForDuplicateExcludeOrderParameterKeys();
		this.checkForEmptyExcludeOrderParameterValues();
		this.checkForEmptyExcludeOrderParameterKeys();
	};
}

export default ChannelEditViewStore;
